import Icon from "@galaxy-io/dls/icons/Icon";
import Text, { TextSize, TextVariant, TextWeight } from "@galaxy-io/dls/text/Text";
import { GalaxyTheme, Placement } from "@galaxy-io/dls/theme/enums";
import { t } from "@galaxy-io/dls/theme/tokens/t";
import { useGalaxyTheme } from "@galaxy-io/dls/theme/useGalaxyTheme";
import Tooltip from "@galaxy-io/dls/tooltip/Tooltip";
import { styled } from "@linaria/react";
import { GlobeIcon, WebhooksLogoIcon } from "@phosphor-icons/react";

const Logo = styled.img`
  display: block;
  object-fit: contain;
  flex-shrink: 0;
`;
// Sources whose brand mark ships on the CDN in a light and a dark variant.
// The Anthropic provider flies Claude's mark, so its file name differs.
const logos: Record<string, { file: string; alt: string }> = {
  posthog: { file: "posthog", alt: "PostHog" },
  github: { file: "github", alt: "GitHub" },
  slack: { file: "slack", alt: "Slack" },
  reddit: { file: "reddit", alt: "Reddit" },
  anthropic: { file: "claude", alt: "Claude" },
};
const Label = styled.div`
  display: flex;
  align-items: center;
  gap: ${t.space[8]};
  min-width: 0;
`;
export function SourceIcon({ source, size = 20 }: { source: string; size?: number }) {
  const { activeTheme } = useGalaxyTheme();
  if (source === "webhook") return <Icon component={WebhooksLogoIcon} size={size} />;
  const logo = logos[source];
  if (!logo) return <Icon component={GlobeIcon} size={size} />;
  return (
    <Logo
      src={`https://cdn.getgalaxy.io/sources/source-icon-${logo.file}-${activeTheme === GalaxyTheme.DARK ? "dark" : "light"}.svg`}
      width={size}
      height={size}
      alt={logo.alt}
    />
  );
}
export function SourceLabel({ source, label }: { source: string; label: string }) {
  return (
    <Label>
      <SourceIcon source={source} />
      <Text size={TextSize.BODY_SM} lineClamp={1}>
        {label}
      </Text>
    </Label>
  );
}

// A record can carry many sources, and one provider can appear more than once
// with different contexts (two subreddits, two repositories). Listing them all
// as labelled rows grows the row height without bound, so a table cell shows an
// overlapping stack of marks and defers the names to hover.
const Stack = styled.div`
  gap: ${t.space[2]};
  display: flex;
  align-items: center;
  min-width: 0;
`;
const Tile = styled.span<{ $size: number }>`
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  border-radius: ${t.radius.md};
  border: 0.5px solid ${t.color.border.primary};
  background: ${t.color.background.base};

  &:hover {
    z-index: 1;
  }
`;
const Overflow = styled.span<{ $size: number }>`
  min-width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  padding: ${t.space[0]} ${t.space[4]};

  border-radius: ${t.radius.lg};
  border: 0.5px solid ${t.color.border.primary};
  background: ${t.color.background.secondary};
`;
const Names = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${t.space[2]};
`;

export type SourceRef = { provider: string; label: string };

/**
 * Overlapping marks for every source on a record. Each mark names its source on
 * hover; anything past `max` collapses into a `+N` tile that lists the rest.
 * Provider-agnostic — any provider `SourceIcon` renders is stackable.
 */
export function SourceStack({
  sources,
  max = 4,
  size = 20,
  position = Placement.TOP,
}: {
  sources: SourceRef[];
  max?: number;
  size?: number;
  position?: Placement;
}) {
  if (!sources.length)
    return (
      <Text size={TextSize.BODY_SM} variant={TextVariant.SECONDARY}>
        —
      </Text>
    );
  // One mark is clearer with its name beside it than as a lone circle.
  if (sources.length === 1)
    return <SourceLabel source={sources[0].provider} label={sources[0].label} />;
  const shown = sources.length > max ? sources.slice(0, max - 1) : sources;
  const rest = sources.slice(shown.length);
  const box = size + 8;
  return (
    <Stack>
      {shown.map((source, index) => (
        <Tooltip
          key={`${source.provider}:${source.label}:${index}`}
          body={source.label}
          placement={position}
        >
          <Tile $size={box}>
            <SourceIcon source={source.provider} size={size} />
          </Tile>
        </Tooltip>
      ))}
      {!!rest.length && (
        <Tooltip
          body={
            <Names>
              {rest.map((source, index) => (
                <Text key={`${source.provider}:${source.label}:${index}`} size={TextSize.BODY_SM}>
                  {source.label}
                </Text>
              ))}
            </Names>
          }
          placement={position}
        >
          <Overflow $size={box}>
            <Text
              size={TextSize.CAPTION}
              weight={TextWeight.MEDIUM}
              variant={TextVariant.SECONDARY}
            >
              +{rest.length}
            </Text>
          </Overflow>
        </Tooltip>
      )}
    </Stack>
  );
}
